import { clearGuestTabletSession, updateGuestTabletSession } from "@/lib/guestTabletApi";
import { readGuestTabletWorkstationCode } from "@/lib/guestTabletWorkstationStorage";

export type GuestTabletSyncStep =
  | "start"
  | "keys_done"
  | "pitch"
  | "summary"
  | "checked_in"
  | "invoice_prep"
  | "invoice"
  | "complete";

export type GuestTabletPitchSyncOptions = {
  candidatePitchIds: string[];
  assignedPitchId?: string;
  skipReviewConfirmation?: boolean;
};

function resolveWorkstationCode(code: string | null | undefined): string | null {
  if (code) return code;
  return readGuestTabletWorkstationCode();
}

const SYNC_RETRY_DELAYS_MS = [0, 1_000, 2_500];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function withTabletSyncRetry(task: () => Promise<void>): Promise<void> {
  for (const delay of SYNC_RETRY_DELAYS_MS) {
    if (delay > 0) await sleep(delay);
    try {
      await task();
      return;
    } catch {
      /* nächster Versuch */
    }
  }
}

export async function syncGuestTabletCheckIn(
  workstationCode: string | null | undefined,
  step: GuestTabletSyncStep,
  options?: {
    bookingId?: string;
    invoiceId?: string;
    pitch?: GuestTabletPitchSyncOptions;
  },
): Promise<void> {
  const resolved = resolveWorkstationCode(workstationCode);
  if (!resolved) return;

  void withTabletSyncRetry(async () => {
    switch (step) {
      case "start":
        if (options?.bookingId) {
          await updateGuestTabletSession(resolved, {
            booking_id: options.bookingId,
            restart_check_in: true,
          });
        }
        break;
      case "keys_done":
        await updateGuestTabletSession(resolved, {
          check_in_status: "stay_preparing",
          guest_display_step: "review",
          ...(options?.pitch
            ? {
                pitch_selection: {
                  candidate_pitch_ids: options.pitch.candidatePitchIds,
                  assigned_pitch_id: options.pitch.assignedPitchId ?? null,
                  skip_review_confirmation: options.pitch.skipReviewConfirmation ?? false,
                },
              }
            : {}),
        });
        break;
      case "pitch":
        if (options?.pitch) {
          const { candidatePitchIds, assignedPitchId, skipReviewConfirmation } = options.pitch;
          await updateGuestTabletSession(resolved, {
            check_in_status: "stay_preparing",
            pitch_selection: {
              candidate_pitch_ids: candidatePitchIds,
              assigned_pitch_id: assignedPitchId ?? null,
              skip_review_confirmation: skipReviewConfirmation ?? false,
            },
            guest_display_step:
              candidatePitchIds.length > 1 && !assignedPitchId
                ? "pitch_choice"
                : assignedPitchId
                  ? "review"
                  : "review",
          });
        }
        break;
      case "summary":
        await updateGuestTabletSession(resolved, {
          check_in_status: "registration_checking",
        });
        break;
      case "checked_in":
        await updateGuestTabletSession(resolved, {
          check_in_status: "payment_checking",
        });
        break;
      case "invoice_prep":
        await updateGuestTabletSession(resolved, {
          check_in_status: "payment_checking",
          guest_display_step: "guest_guide",
          ...(options?.invoiceId ? { guest_invoice_id: options.invoiceId } : {}),
        });
        break;
      case "invoice":
        if (options?.invoiceId) {
          await updateGuestTabletSession(resolved, {
            check_in_status: "payment_checking",
            guest_display_step: "invoice",
            guest_invoice_id: options.invoiceId,
          });
        }
        break;
      case "complete":
        await updateGuestTabletSession(resolved, {
          check_in_status: "check_in_complete",
          guest_display_step: "complete",
        });
        break;
    }
  });
}

export async function clearGuestTabletAfterCheckIn(
  workstationCode: string | null | undefined,
): Promise<void> {
  const resolved = resolveWorkstationCode(workstationCode);
  if (!resolved) return;
  await withTabletSyncRetry(async () => {
    await clearGuestTabletSession(resolved);
  });
}
